/**
 * Production-safe logging utility
 * Automatically removes console logs in production builds
 */

// Check if we're in development mode
const isDevelopment = process.env.NODE_ENV === 'development' || import.meta.env.DEV;

/**
 * Development-only console logger
 * These logs will not appear in production builds
 */
export const devLog = {
  log: (...args: unknown[]) => {
    if (isDevelopment) {
      console.log(...args);
    }
  },
  
  warn: (...args: unknown[]) => {
    if (isDevelopment) {
      console.warn(...args);
    }
  },
  
  error: (...args: unknown[]) => {
    // Always show errors, even in production
    console.error(...args);
  },
  
  info: (...args: unknown[]) => {
    if (isDevelopment) {
      console.info(...args);
    }
  },
  
  debug: (...args: unknown[]) => {
    if (isDevelopment) {
      console.debug(...args);
    }
  }
};

/**
 * API debugging specifically for AI API calls
 */
export const apiLog = {
  request: (url: string, data: unknown) => {
    if (isDevelopment) {
      console.group('🚀 API Request');
      console.log('URL:', url);
      console.log('Data:', data);
      console.groupEnd();
    }
  },
  
  response: (url: string, response: unknown) => {
    if (isDevelopment) {
      console.group('✅ API Response');
      console.log('URL:', url);
      console.log('Response:', response);
      console.groupEnd();
    }
  },
  
  error: (url: string, error: unknown) => {
    // Always log API errors
    console.group('❌ API Error');
    console.error('URL:', url);
    console.error('Error:', error);
    console.groupEnd();
  }
};

/**
 * Performance logging for optimization
 */
export const perfLog = {
  start: (label: string) => {
    if (isDevelopment) {
      console.time(label);
    }
  },
  
  end: (label: string) => {
    if (isDevelopment) {
      console.timeEnd(label);
    }
  },
  
  mark: (label: string, ...args: unknown[]) => {
    if (isDevelopment) {
      console.log(`⏱️ ${label}:`, ...args);
    }
  }
};

/**
 * Slide generation specific logging
 */
export const slideLog = {
  generation: (step: string, data: unknown) => {
    if (isDevelopment) {
      console.log(`🎯 Slide Generation - ${step}:`, data);
    }
  },
  
  rendering: (slideId: string, data: any) => {
    if (isDevelopment) {
      console.log(`🎨 Slide Rendering - ${slideId}:`, data);
    }
  },
  
  error: (context: string, error: any) => {
    console.error(`❌ Slide Error - ${context}:`, error);
  }
};

/**
 * User interaction logging for UX improvements
 */
export const userLog = {
  action: (action: string, data?: any) => {
    if (isDevelopment) {
      console.log(`👤 User Action - ${action}:`, data);
    }
  },
  
  error: (context: string, error: any) => {
    console.error(`👤 User Error - ${context}:`, error);
  }
};

// Export a default logger that matches console API
export const logger = devLog;